import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getWorkWiseAuthToken } from "./utils/auth";
import { workwiseServices } from "./api";
import { setDocumentInformation, setUserInformation, setIsAuthenticated } from '../src/app/EditorSlice';
import { useDispatch, useSelector } from "react-redux";
import SplashScreen from "./components/SplashScreen/SplashScreen";
import { getMergedCachedData } from "./utils/documentCache";
import { isEffectivelyOffline } from "./utils/helper";

const EXIT_DURATION = 550;
// Auth often resolves in a few hundred ms (and instantly when it is skipped),
// which made the loader flash on and straight back off. Hold it on screen for
// at least this long so the intro animation reads as intentional.
const MIN_VISIBLE_DURATION = 1500;

export const AuthenticateDocument = ({ setIsLoading, background = false }) => {

  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const isTemplateDocument = useSelector((state) => state.editor.isTemplateDocument);
  // Local (offline-created) documents live only in localStorage. Identified by ?local=true.
  const isLocalDocument = new URLSearchParams(document.location.search).get('local') === 'true';
  const [exiting, setExiting] = useState(false);
  const exitTimeoutRef = useRef(null);
  const holdTimeoutRef = useRef(null);
  // Wall clock of the first render, i.e. when the loader actually appeared.
  const shownAtRef = useRef(Date.now());

  useEffect(() => () => {
    clearTimeout(exitTimeoutRef.current);
    clearTimeout(holdTimeoutRef.current);
  }, []);

  // Plays the loader's fade-out before actually flipping the parent's
  // loading flag, so the document doesn't just snap into view. Background mode
  // draws no loader, so it hands over as soon as auth resolves.
  const finishLoading = () => {
    if (background) {
      setIsLoading(false);
      return;
    }

    const remainingHold = Math.max(
      0,
      MIN_VISIBLE_DURATION - (Date.now() - shownAtRef.current)
    );

    clearTimeout(holdTimeoutRef.current);
    holdTimeoutRef.current = setTimeout(() => {
      setExiting(true);
      exitTimeoutRef.current = setTimeout(() => setIsLoading(false), EXIT_DURATION);
    }, remainingHold);
  };

  const handleAuhenticate = async () => {
    try {
      setIsLoading(true);
      // Offline + local document: there is no server counterpart, so skip the
      // authentication API entirely and treat the user as authenticated.
      if (isEffectivelyOffline() && isLocalDocument) {
        finishLoading();
        dispatch(setIsAuthenticated(true));
        return;
      }
      if (!window.navigator.onLine) {
        // navigate('/no-internet');
        window.location.pathname = "/no-internet"
        return;
      }
      else {
        if (!!getWorkWiseAuthToken()) {
          const response = await workwiseServices.getDocumentById(id);
          if (response?.status == 200 && response.data?.data?.rightType !== 0 && response.data?.data) {
            finishLoading();
            dispatch(setDocumentInformation(response.data.data));
            dispatch(setIsAuthenticated(true));
          }
          if (response.data?.data?.rightType == 0) {
            // navigate("/unauthorized")
            window.location.pathname = "/unauthorized"
          }
          // history.push("/unauthorized")
        }
        else {
          // navigate("/unauthorized")
          window.location.pathname = "/unauthorized"
        }
      }
    } catch (error) {
      console.log({ error })
      if (error.response?.data?.responseCode === 1008) {
        if (isTemplateDocument) {
          // For templates, 1008 is not a failure, set authenticated
          finishLoading();
          dispatch(setIsAuthenticated(true));
          return;
        } else {
          // navigate("/invalid-document")
          window.location.pathname = "/invalid-document"
        }
      }
      else {
        // navigate("/unauthorized")
        window.location.pathname = "/unauthorized"

      }
    }
  }

  // In background mode, seed documentInfo from cache up front so the Header has
  // real data instantly, before the auth API resolves.
  useEffect(() => {
    if (background) {
      const cached = getMergedCachedData(id);
      if (cached?.documentInfo) {
        dispatch(setDocumentInformation(cached.documentInfo));
      }
    }
  }, [background, id, dispatch]);

  useEffect(() => {
    if (isTemplateDocument !== null) {
      handleAuhenticate();
    }
  }, [isTemplateDocument]);

  // Background mode renders nothing — the layout is already on screen.
  if (background) return null;

  return <SplashScreen exiting={exiting} />

}